import {
  ChangeDetectionStrategy,
  Component,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { I18nService } from '../../services/i18n.service';
import { GtmService } from '../../services/gtm.service';

interface ConsentState {
  given: boolean;
  necessary: true;
  analytics: boolean;
  ts: number;
}

const STORAGE_KEY = 'ds-cookie-consent';

@Component({
  selector: 'ds-cookie-consent',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (t(); as t) {
      <!-- ── First-visit banner ────────────────────────────────── -->
      @if (showBanner()) {
        <div class="cc-banner" role="dialog" aria-modal="false" [attr.aria-label]="t.banner.title">
          <div class="cc-banner__inner">
            <div class="cc-banner__icon" aria-hidden="true">
              <svg viewBox="0 0 32 32" width="28" height="28">
                <circle cx="16" cy="16" r="13" fill="#C49A6C" stroke="#8B6240" stroke-width="1.5" />
                <circle cx="11" cy="12" r="1.6" fill="#3D2817" />
                <circle cx="18" cy="10.5" r="1.3" fill="#3D2817" />
                <circle cx="20" cy="17" r="1.6" fill="#3D2817" />
                <circle cx="13" cy="20" r="1.4" fill="#3D2817" />
                <circle cx="22" cy="22" r="1.1" fill="#3D2817" />
                <circle cx="9" cy="17" r="1.1" fill="#3D2817" />
              </svg>
            </div>
            <div class="cc-banner__body">
              <div class="cc-banner__title">{{ t.banner.title }}</div>
              <p class="cc-banner__desc">{{ t.banner.desc }}</p>
            </div>
            <div class="cc-banner__actions">
              <button type="button" class="cc-btn cc-btn--ghost" (click)="rejectAll()">
                {{ t.banner.btn_reject }}
              </button>
              <button type="button" class="cc-btn cc-btn--ghost" (click)="openPanel()">
                {{ t.banner.btn_customize }}
              </button>
              <button type="button" class="cc-btn cc-btn--primary" (click)="acceptAll()">
                {{ t.banner.btn_accept }}
              </button>
            </div>
          </div>
        </div>
      }

      <!-- ── Floating button (after consent or after closing banner) ─ -->
      @if (!showBanner() && !panelOpen()) {
        <button
          type="button"
          class="cc-fab"
          (click)="openPanel()"
          [attr.aria-label]="t.btn_label"
          [title]="t.btn_label"
        >
          <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true">
            <circle cx="16" cy="16" r="13" fill="#C49A6C" stroke="#8B6240" stroke-width="1.5" />
            <circle cx="11" cy="12" r="1.6" fill="#3D2817" />
            <circle cx="18" cy="10.5" r="1.3" fill="#3D2817" />
            <circle cx="20" cy="17" r="1.6" fill="#3D2817" />
            <circle cx="13" cy="20" r="1.4" fill="#3D2817" />
            <circle cx="22" cy="22" r="1.1" fill="#3D2817" />
            <circle cx="9" cy="17" r="1.1" fill="#3D2817" />
          </svg>
        </button>
      }

      <!-- ── Modal panel ───────────────────────────────────────── -->
      @if (panelOpen()) {
        <div class="cc-overlay" (click)="closePanel()" aria-hidden="true"></div>
        <div
          class="cc-panel"
          role="dialog"
          aria-modal="true"
          [attr.aria-label]="detailedView() ? t.panel.title_detailed : t.panel.title_compact"
        >
          <header class="cc-panel__head">
            <div class="cc-panel__head-left">
              <span class="cc-panel__head-icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path
                    d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
                  />
                </svg>
              </span>
              <span class="cc-panel__title">{{
                detailedView() ? t.panel.title_detailed : t.panel.title_compact
              }}</span>
            </div>
            <button
              type="button"
              class="cc-btn cc-btn--outline cc-btn--sm"
              (click)="toggleDetails()"
            >
              {{ detailedView() ? t.panel.less_details : t.panel.more_details }}
            </button>
          </header>

          <div class="cc-panel__body">
            @if (!detailedView()) {
              <p class="cc-panel__lead">{{ t.panel.compact_lead }}</p>
              <ul class="cc-summary">
                @for (s of t.panel.compact_summary; track $index) {
                  <li>{{ s }}</li>
                }
              </ul>
            } @else {
              @for (s of t.panel.sections; track s.n; let i = $index) {
                <section class="cc-section">
                  <header class="cc-section__head">
                    <span class="cc-section__num" aria-hidden="true">{{ s.n }}</span>
                    <h3 class="cc-section__title">{{ s.title }}</h3>
                    @if (i === 0) {
                      <span class="cc-pill cc-pill--success">{{ t.panel.always_active }}</span>
                    } @else if (i === 1) {
                      <span class="cc-section__optional">{{ t.panel.optional }}</span>
                      <button
                        type="button"
                        class="cc-toggle"
                        role="switch"
                        [attr.aria-checked]="analytics()"
                        (click)="analytics.update((v) => !v)"
                        [attr.aria-label]="s.title"
                      >
                        <span class="cc-toggle__track" [class.cc-toggle__track--on]="analytics()">
                          <span class="cc-toggle__thumb"></span>
                        </span>
                      </button>
                    }
                  </header>
                  @if (s.intro) {
                    <p class="cc-section__intro">{{ s.intro }}</p>
                  }
                  @if (s.items?.length) {
                    <ul class="cc-section__list">
                      @for (it of s.items; track $index) {
                        <li>{{ it }}</li>
                      }
                    </ul>
                  }
                  @if (s.subhead) {
                    <p class="cc-section__subhead">{{ s.subhead }}</p>
                  }
                  @if (s.subitems?.length) {
                    <ul class="cc-section__list">
                      @for (it of s.subitems; track $index) {
                        <li>{{ it }}</li>
                      }
                    </ul>
                  }
                  @if (s.footnote) {
                    <p class="cc-section__footnote">{{ s.footnote }}</p>
                  }
                </section>
              }
            }
          </div>

          <footer class="cc-panel__foot">
            <button type="button" class="cc-btn cc-btn--ghost" (click)="savePreferences()">
              {{ t.panel.btn_save }}
            </button>
            <button type="button" class="cc-btn cc-btn--primary" (click)="acceptAllAndClose()">
              {{ t.panel.btn_accept }}
            </button>
          </footer>
        </div>
      }
    }
  `,
  styles: [
    `
      :host {
        font-family: var(--f-body);
      }

      /* ── Banner ───────────────────────────────────────────────── */
      .cc-banner {
        position: fixed;
        left: 50%;
        bottom: 24px;
        transform: translateX(-50%);
        width: min(940px, calc(100% - 32px));
        z-index: 110;
        animation: cc-slide-up 350ms var(--e-decel, cubic-bezier(0.2, 0.7, 0.3, 1));
      }
      .cc-banner__inner {
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: 14px;
        box-shadow:
          0 18px 48px -16px rgba(0, 0, 0, 0.3),
          0 4px 12px -4px rgba(0, 0, 0, 0.18);
        padding: 18px 22px;
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 18px;
        align-items: center;

        @media (max-width: 720px) {
          grid-template-columns: auto 1fr;
          gap: 14px;
          padding: 16px;
        }
      }
      .cc-banner__icon {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        display: grid;
        place-items: center;
        background: color-mix(in srgb, var(--c-accent) 8%, transparent);
        flex-shrink: 0;
      }
      .cc-banner__title {
        font-family: var(--f-display);
        font-size: 15px;
        font-weight: 700;
        color: var(--on-surface);
        margin-bottom: 2px;
      }
      .cc-banner__desc {
        margin: 0;
        font-size: 13px;
        color: var(--on-surface-muted);
        line-height: 1.5;
      }
      .cc-banner__actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;

        @media (max-width: 720px) {
          grid-column: 1 / -1;
          flex-wrap: wrap;
          & > * {
            flex: 1 1 auto;
            min-width: 100px;
          }
        }
      }

      /* ── Floating button ──────────────────────────────────────── */
      .cc-fab {
        position: fixed;
        left: 20px;
        bottom: 20px;
        z-index: 105;
        width: 44px;
        height: 44px;
        border-radius: 12px;
        border: 1px solid color-mix(in srgb, currentColor 14%, transparent);
        background: color-mix(in srgb, var(--surface) 80%, transparent);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.35);
        cursor: pointer;
        display: grid;
        place-items: center;
        color: var(--on-surface);
        transition:
          transform var(--d-2, 200ms) var(--e-decel),
          box-shadow var(--d-2, 200ms) var(--e-decel);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 22px -8px rgba(0, 0, 0, 0.4);
        }
        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
        }
      }

      /* ── Modal panel ──────────────────────────────────────────── */
      .cc-overlay {
        position: fixed;
        inset: 0;
        z-index: 200;
        background: color-mix(in srgb, #000 55%, transparent);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        animation: cc-fade-in 180ms ease;
      }
      .cc-panel {
        position: fixed;
        z-index: 201;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: min(880px, calc(100% - 32px));
        max-height: min(86vh, 920px);
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: 16px;
        box-shadow: 0 30px 60px -20px rgba(0, 0, 0, 0.4);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: cc-pop 220ms var(--e-decel, cubic-bezier(0.2, 0.7, 0.3, 1));
      }
      .cc-panel__head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 18px 22px;
        border-bottom: 1px solid var(--border-subtle);
        background: var(--surface);
      }
      .cc-panel__head-left {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
      }
      .cc-panel__head-icon {
        display: grid;
        place-items: center;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background: color-mix(in srgb, currentColor 6%, transparent);
        color: var(--on-surface-muted);
      }
      .cc-panel__title {
        font-family: var(--f-display);
        font-size: 17px;
        font-weight: 700;
        color: var(--on-surface);
        letter-spacing: -0.01em;
      }
      .cc-panel__body {
        padding: 18px 22px 22px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .cc-panel__lead {
        margin: 0;
        font-size: 14px;
        color: var(--on-surface);
        line-height: 1.55;
      }
      .cc-summary {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
        font-size: 13.5px;
        color: var(--on-surface-muted);

        li {
          position: relative;
          padding-left: 18px;
          line-height: 1.5;
          &::before {
            content: '';
            position: absolute;
            left: 4px;
            top: 9px;
            width: 5px;
            height: 5px;
            border-radius: 999px;
            background: var(--c-accent);
          }
        }
      }
      .cc-panel__foot {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        padding: 16px 22px;
        border-top: 1px solid var(--border-subtle);
        background: var(--surface);

        @media (max-width: 560px) {
          flex-direction: column-reverse;
        }
      }

      /* ── Section card ─────────────────────────────────────────── */
      .cc-section {
        background: color-mix(in srgb, var(--on-surface) 3%, var(--surface));
        border: 1px solid var(--border-subtle);
        border-radius: 12px;
        padding: 16px 18px;
      }
      .cc-section__head {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
        flex-wrap: wrap;
      }
      .cc-section__num {
        display: grid;
        place-items: center;
        width: 22px;
        height: 22px;
        border-radius: 5px;
        background: color-mix(in srgb, var(--on-surface) 16%, transparent);
        color: var(--on-surface);
        font-family: var(--f-mono);
        font-size: 11px;
        font-weight: 700;
      }
      .cc-section__title {
        margin: 0;
        font-family: var(--f-display);
        font-size: 15px;
        font-weight: 700;
        color: var(--on-surface);
        letter-spacing: -0.01em;
      }
      .cc-section__optional {
        font-family: var(--f-body);
        font-size: 12.5px;
        color: var(--on-surface-muted);
        font-style: italic;
      }
      .cc-section__intro,
      .cc-section__subhead {
        margin: 0 0 6px;
        font-size: 13.5px;
        color: var(--on-surface);
        line-height: 1.5;
      }
      .cc-section__subhead {
        margin-top: 12px;
        font-weight: 700;
      }
      .cc-section__list {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;

        li {
          position: relative;
          padding-left: 16px;
          font-size: 13px;
          color: var(--on-surface-muted);
          line-height: 1.55;
          &::before {
            content: '';
            position: absolute;
            left: 4px;
            top: 8px;
            width: 4px;
            height: 4px;
            border-radius: 999px;
            background: var(--on-surface-muted);
            opacity: 0.7;
          }
        }
      }
      .cc-section__footnote {
        margin: 12px 0 0;
        font-size: 12px;
        color: var(--on-surface-muted);
        line-height: 1.5;
        font-style: italic;
      }

      /* ── Toggle (analytics) ───────────────────────────────────── */
      .cc-toggle {
        margin-left: auto;
        background: transparent;
        border: 0;
        padding: 4px;
        cursor: pointer;

        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
          border-radius: 18px;
        }
      }
      .cc-toggle__track {
        display: inline-block;
        position: relative;
        width: 38px;
        height: 22px;
        border-radius: 999px;
        background: color-mix(in srgb, var(--on-surface) 22%, transparent);
        transition: background-color var(--d-2, 200ms) var(--e-standard);

        &--on {
          background: var(--c-accent);
        }
      }
      .cc-toggle__thumb {
        position: absolute;
        top: 3px;
        left: 3px;
        width: 16px;
        height: 16px;
        border-radius: 999px;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
        transition: transform var(--d-2, 200ms) var(--e-decel);

        .cc-toggle__track--on & {
          transform: translateX(16px);
        }
      }

      /* ── Pill ─────────────────────────────────────────────────── */
      .cc-pill {
        margin-left: auto;
        font-family: var(--f-mono);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        padding: 4px 10px;
        border-radius: 999px;
        white-space: nowrap;

        &--success {
          background: #15803d;
          color: #fff;
        }
      }

      /* ── Buttons ──────────────────────────────────────────────── */
      .cc-btn {
        font-family: var(--f-body);
        font-size: 14px;
        font-weight: 600;
        padding: 10px 16px;
        border-radius: 10px;
        cursor: pointer;
        border: 1px solid transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition:
          background-color var(--d-2, 200ms) var(--e-standard),
          color var(--d-2, 200ms) var(--e-standard),
          transform var(--d-2, 200ms) var(--e-decel);
        white-space: nowrap;

        &:focus-visible {
          outline: 2px solid var(--c-focus);
          outline-offset: 2px;
        }
        &:active {
          transform: translateY(1px);
        }
      }
      .cc-btn--sm {
        padding: 6px 12px;
        font-size: 12.5px;
        border-radius: 8px;
      }
      .cc-btn--primary {
        background: var(--c-accent);
        color: var(--c-accent-ink, #fff);

        &:hover {
          background: var(--c-accent-hover, var(--c-accent));
        }
      }
      .cc-btn--ghost {
        background: var(--surface-secondary);
        color: var(--on-surface);
        border-color: var(--border);

        &:hover {
          background: color-mix(in srgb, var(--on-surface) 5%, var(--surface-secondary));
        }
      }
      .cc-btn--outline {
        background: transparent;
        color: var(--c-accent);
        border-color: var(--c-accent);

        &:hover {
          background: color-mix(in srgb, var(--c-accent) 10%, transparent);
        }
      }

      /* ── Animations ───────────────────────────────────────────── */
      @keyframes cc-slide-up {
        from {
          transform: translate(-50%, 32px);
          opacity: 0;
        }
        to {
          transform: translate(-50%, 0);
          opacity: 1;
        }
      }
      @keyframes cc-fade-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      @keyframes cc-pop {
        from {
          transform: translate(-50%, -50%) scale(0.96);
          opacity: 0;
        }
        to {
          transform: translate(-50%, -50%) scale(1);
          opacity: 1;
        }
      }
    `,
  ],
})
export class DsCookieConsentComponent {
  private readonly i18n = inject(I18nService);
  private readonly gtm = inject(GtmService);

  readonly t = computed(() => this.i18n.translations()?.cookies ?? null);

  readonly state = signal<ConsentState | null>(null);
  readonly panelOpen = signal(false);
  readonly detailedView = signal(false);
  readonly analytics = signal(false);

  readonly showBanner = computed(() => this.state() === null && !this.panelOpen());

  constructor() {
    afterNextRender(() => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as ConsentState;
          this.state.set(parsed);
          this.analytics.set(parsed.analytics);
        }
      } catch {
        /* ignore corrupt storage */
      }

      document.addEventListener('keydown', (ev) => {
        if (ev.key === 'Escape' && this.panelOpen()) this.closePanel();
      });
    });
  }

  openPanel(): void {
    if (this.state()) this.analytics.set(this.state()!.analytics);
    this.panelOpen.set(true);
  }
  closePanel(): void {
    this.panelOpen.set(false);
  }
  toggleDetails(): void {
    this.detailedView.update((v) => !v);
  }

  acceptAll(): void {
    this.persist({ analytics: true });
  }
  acceptAllAndClose(): void {
    this.persist({ analytics: true });
    this.closePanel();
  }
  rejectAll(): void {
    this.persist({ analytics: false });
  }
  savePreferences(): void {
    this.persist({ analytics: this.analytics() });
    this.closePanel();
  }

  private persist({ analytics }: { analytics: boolean }): void {
    const next: ConsentState = {
      given: true,
      necessary: true,
      analytics,
      ts: Date.now(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    this.state.set(next);
    this.analytics.set(analytics);
    this.gtm.onConsentChange(analytics);
  }
}
